import React, { useEffect, useMemo, useState, forwardRef } from 'react';
import { optionsWithSelection, SelectSearch } from '~/ui/components';
import { debounce } from 'lodash';
import { useAwaitControl } from 'react-redux-await-control';
import { fetchTicketFinancialInstitutions } from '~/store/utilities/actions';

export type FinancialInstitutionSelectProps = {
  value?: string | null | undefined;
  onChange?: (value: string) => void;
};

export const FinancialInstitutionSelect = forwardRef<HTMLDivElement, FinancialInstitutionSelectProps>(
  ({ value, onChange }, ref) => {
    const [currentValue, setCurrentValue] = useState<{ value: string; label: string }>(null);
    const [searchString, setSearchString] = useState('');

    const awaitControl = useAwaitControl(fetchTicketFinancialInstitutions);
    const isLoading = awaitControl.isRunning();
    const financialInstitutionsList = awaitControl.result();

    const normalizedOptions = useMemo(
      () =>
        financialInstitutionsList
          ?.filter((fi) => fi.financialInstitutionName)
          .map((fi) => ({ name: fi.financialInstitutionName, id: fi.financialInstitutionName })) || [],
      [financialInstitutionsList],
    );

    const options = useMemo(() => {
      if (!searchString?.length) {
        return normalizedOptions;
      }

      return normalizedOptions?.filter((data) => data.name?.toLowerCase().includes(searchString.toLowerCase().trim()));
    }, [normalizedOptions, searchString]);

    const handleOnChange = (option: { value: string; label: string }) => {
      setCurrentValue(option);

      if (onChange) {
        onChange(option?.value);
      }
    };

    const handleSearch = debounce((searchValue) => {
      setSearchString(searchValue);
    }, 500);

    useEffect(() => {
      if (!financialInstitutionsList?.length) {
        awaitControl.start();
      }
    }, []);

    return (
      <SelectSearch
        ref={ref}
        searchValue={searchString}
        placeholder="Fundo"
        width={180}
        value={value ? { value } : null}
        onSearch={handleSearch}
        onChange={handleOnChange}
        loading={isLoading}
        options={optionsWithSelection(options, currentValue)}
        onClear={() => setSearchString('')}
      />
    );
  },
);
